import { TechRadarLoaderResponseParser } from '@backstage-community/plugin-tech-radar-common';
import { z } from 'zod';

/**
 * Extended TechRadar data schema with cross-validation.
 *
 * Reuses the community TechRadarLoaderResponseParser for base validation
 * and adds cross-referential integrity checks via superRefine.
 */
export const techRadarDataSchema = TechRadarLoaderResponseParser.superRefine(
  (data, ctx) => {
    // Exactly 4 quadrants required
    if (data.quadrants.length !== 4) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Expected exactly 4 quadrants, got ${data.quadrants.length}`,
        path: ['quadrants'],
      });
    }

    // At least 1 ring required
    if (data.rings.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least 1 ring is required',
        path: ['rings'],
      });
    }

    const quadrantIds = new Set(data.quadrants.map(q => q.id));
    const ringIds = new Set(data.rings.map(r => r.id));

    for (const [i, entry] of data.entries.entries()) {
      // Entry quadrant must reference an existing quadrant
      if (!quadrantIds.has(entry.quadrant)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Entry "${entry.id}" references unknown quadrant "${entry.quadrant}"`,
          path: ['entries', i, 'quadrant'],
        });
      }

      // At least 1 timeline snapshot per entry
      if (entry.timeline.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Entry "${entry.id}" must have at least 1 timeline snapshot`,
          path: ['entries', i, 'timeline'],
        });
      }

      // Each timeline snapshot ringId must reference an existing ring
      for (const [j, snapshot] of entry.timeline.entries()) {
        if (!ringIds.has(snapshot.ringId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Entry "${entry.id}" timeline snapshot references unknown ring "${snapshot.ringId}"`,
            path: ['entries', i, 'timeline', j, 'ringId'],
          });
        }
      }
    }
  },
);
